// ---------------------------------------------------------
// Datei: DocumentChecklist.tsx
// ---------------------------------------------------------
import React, { useEffect, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { useDocumentStore } from "../store/documentState";
import { DocumentType, UploadedDocumentFile } from "../types/documents";
import { getSignedUrl, updateDealTextProperty } from "../services/hubspotBackendClient";
import { deleteDriveFile } from "../services/api.extensions";
import { getAuthApiBaseUrl, getGoogleAuthHeaders } from "../services/googleAuthSession";
import FormularModal from "./FormularModal";
import DocumentPreviewModal from "./DocumentPreviewModal";
import { DealContact } from "../types/hubspot";
import { useQueue } from "../contexts/QueueContext";
import {
  getDriveFileIdFromUrl,
  uploadedDocumentFilesToPropertyValue,
} from "../utils/uploadedDocumentFiles";
import type { UploadFile } from "./FileUploadModal";
import { DOCUMENT_TO_PROPERTY_MAP } from "../services/hubspotDealMapper";

function formatConsentTimestamp(raw: string): string {
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' · '
    + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    + ' Uhr';
}

interface DocumentChecklistProps {
  phaseId: string;
  onSelectDocument?: (document: DocumentType) => void;
  onDirectPhotoUpload?: (document: DocumentType, files: UploadFile[]) => void | Promise<void>;
  onError?: (error: string) => void;
  disabled?: boolean;
  dealContact?: DealContact;
}

const DocumentChecklist: React.FC<DocumentChecklistProps> = ({
  phaseId,
  onSelectDocument,
  onDirectPhotoUpload,
  onError,
  disabled = false,
  dealContact,
}) => {
  const {
    fetchDocuments,
    isPhaseLoading,
    getPhaseError,
    updateDocumentStatus,
    deleteDocument,
    getDocumentData,
    getDocumentFileId,
    getDocumentFiles,
    saveDocumentFileId,
    saveDocumentData, // WICHTIG: Um die URL lokal zu speichern
    saveDocumentFiles,
  } = useDocumentStore();

  // WICHTIG: useShallow für reaktives Update bei nested Array-Änderungen
  // Zustand v5: useShallow vergleicht Array-Inhalte statt nur Referenzen
  const documents = useDocumentStore(
    useShallow((state) => state.documents[phaseId] || [])
  ) as DocumentType[];
  const loading = isPhaseLoading(phaseId);
  const error = getPhaseError(phaseId);
  const { items: queueItems, removeFromQueue } = useQueue();

  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  const [formularModalOpen, setFormularModalOpen] = useState(false);
  const [selectedFormular, setSelectedFormular] = useState<DocumentType | null>(null);
  
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<DocumentType | null>(null);
  const [previewData, setPreviewData] = useState<string>("");
  const [serialPreviewText, setSerialPreviewText] = useState<string | null>(null);
  const [serialPreviewTitle, setSerialPreviewTitle] = useState<string>('');
  const [galleryDocument, setGalleryDocument] = useState<DocumentType | null>(null);
  const [galleryFiles, setGalleryFiles] = useState<UploadedDocumentFile[]>([]);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [galleryPreviewData, setGalleryPreviewData] = useState<string>("");
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [thumbnailCache, setThumbnailCache] = useState<Record<string, string>>({});

  const isFetchingRef = useRef(false);

  // === Load Documents ===
  useEffect(() => {
    if (!phaseId) { if (onError) onError("Phasen-ID fehlt"); return; }
    if ((documents && documents.length > 0) || loading || isFetchingRef.current) return;
    const isHubSpotPhase = phaseId.includes("-");
    if (isHubSpotPhase) return;
    isFetchingRef.current = true;
    fetchDocuments(phaseId).catch((err) => onError && onError(err.message)).finally(() => { isFetchingRef.current = false; });
  }, [phaseId, loading, documents, fetchDocuments]);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = () => setActionMenuOpen(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // === NEUER HELPER: URL FETCHEN & ERSETZEN ===
  const fetchAndCacheHubSpotUrl = async (fileId: string, docId: string) => {
    try {
      console.log(`[HELPER] Hole URL für FileID ${fileId}...`);
      
      // 1. GET Call an Backend (wie dein Postman Call)
      const signedUrl = await getSignedUrl(fileId);
      
      if (signedUrl) {
        console.log(`[HELPER] Neue URL erhalten. Aktualisiere Anzeige.`);
        
        // 2. URL im lokalen Store speichern (überschreibt alte Daten/Bilder)
        saveDocumentData(phaseId, docId, signedUrl);
        return signedUrl;
      }
    } catch (e) {
      console.error("[HELPER] Fehler beim Abrufen der URL:", e);
    }
    return null;
  };

  // === Handlers ===

  const openDrivePreview = async (driveUrl: string, document: DocumentType) => {
    const driveFileId = driveUrl.split('/d/')[1]?.split('/')[0];
    if (!driveFileId) return;
    try {
      const authBase = getAuthApiBaseUrl();
      const authHeaders = await getGoogleAuthHeaders();
      const res = await fetch(`${authBase}/google/drive/file/${driveFileId}`, {
        credentials: 'include',
        headers: authHeaders,
      });
      const json = await res.json();
      if (json.status && json.base64Content) {
        setPreviewDocument(document);
        setPreviewData(`data:${json.mimeType};base64,${json.base64Content}`);
        setPreviewModalOpen(true);
      }
    } catch (e) {
      console.error('[Preview] Drive fetch fehlgeschlagen', e);
    }
  };

  const isDurchfuehrungMultiUploadDocument = (document: DocumentType) =>
    phaseId.endsWith('-durchfuehrung') &&
    document._id !== 'seriennummer-ausseneinheit' &&
    document._id !== 'typenschild-altanlage-durchfuehrung';

  const isImageFile = (file: File) =>
    file.type.startsWith('image/') || /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);

  const readFileAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const buildUploadFiles = async (files: File[]): Promise<UploadFile[]> =>
    Promise.all(
      files.map(async (file) => ({
        id: `direct-photo-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        name: file.name,
        type: file.type || 'image/jpeg',
        size: file.size,
        dataUrl: await readFileAsDataUrl(file),
        file,
      }))
    );

  const getDirectPhotoInputId = (document: DocumentType) =>
    `direct-photo-input-${phaseId}-${document._id}`.replace(/[^a-zA-Z0-9_-]/g, '-');

  const handleDirectPhotoInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    document: DocumentType
  ) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    if (selectedFiles.length === 0) return;
    setActionMenuOpen(null);

    const imageFiles = selectedFiles.filter(isImageFile);
    if (imageFiles.length === 0) {
      onError?.('Bitte nur Bilder auswählen.');
      return;
    }
    if (imageFiles.length !== selectedFiles.length) {
      onError?.('Einige Dateien wurden übersprungen, weil sie keine Bilder sind.');
    }

    try {
      closeDocumentGallery();
      const uploadFiles = await buildUploadFiles(imageFiles);
      await onDirectPhotoUpload?.(document, uploadFiles);
    } catch (uploadError) {
      console.error('[DocumentChecklist] Direkter Foto-Upload fehlgeschlagen', uploadError);
      onError?.('Bilder konnten nicht vorbereitet werden.');
    }
  };

  const getQueuedDocumentFiles = (docId: string): UploadedDocumentFile[] => {
    return queueItems
      .filter((item: any) => item.type === 'document_upload' && item.data?.documentId === docId && item.data?.phaseId === phaseId)
      .flatMap((item: any) => {
        const files = Array.isArray(item.data?.files)
          ? item.data.files
          : item.data?.imageData
            ? [{ id: `${item.id}-legacy`, name: 'Ausstehendes Bild', type: 'image/jpeg', dataUrl: item.data.imageData }]
            : [];

        return files.map((file: any, index: number) => ({
          id: `queued-${item.id}-${file.id || index}`,
          name: file.name || 'Ausstehendes Bild',
          mimeType: file.type || file.mimeType || 'image/jpeg',
          source: 'local' as const,
          previewData: file.dataUrl,
        }));
      });
  };

  const openDocumentGallery = (document: DocumentType, files: UploadedDocumentFile[]) => {
    setGalleryDocument(document);
    setGalleryFiles(files);
    setGalleryIndex(0);
    setGalleryPreviewData("");
  };

  const closeDocumentGallery = () => {
    setGalleryDocument(null);
    setGalleryFiles([]);
    setGalleryIndex(0);
    setGalleryPreviewData("");
    setGalleryLoading(false);
  };

  const getFilePreviewKey = (file: UploadedDocumentFile) => file.fileId || file.url || file.id;

  const fetchDriveFilePreview = async (driveFileId: string) => {
    const authBase = getAuthApiBaseUrl();
    const authHeaders = await getGoogleAuthHeaders();
    const res = await fetch(`${authBase}/google/drive/file/${driveFileId}`, {
      credentials: 'include',
      headers: authHeaders,
    });
    const json = await res.json();
    if (json.status && json.base64Content) {
      return `data:${json.mimeType};base64,${json.base64Content}`;
    }
    return "";
  };

  const resolveFilePreview = async (file: UploadedDocumentFile | undefined) => {
    if (!file) {
      return "";
    }

    if (file.previewData) {
      return file.previewData;
    }

    if (file.thumbnailUrl) {
      return file.thumbnailUrl;
    }

    try {
      const driveFileId = file.url ? getDriveFileIdFromUrl(file.url) : file.source === 'drive' ? file.fileId : null;
      if (driveFileId) {
        return await fetchDriveFilePreview(driveFileId);
      }

      if (file.url) {
        return file.url;
      }

      if (file.fileId) {
        const url = await getSignedUrl(file.fileId);
        return url || "";
      }
    } catch (e) {
      console.error('[Preview] Bild konnte nicht geladen werden', e);
    }

    return "";
  };

  const loadGalleryPreview = async (file: UploadedDocumentFile | undefined) => {
    setGalleryLoading(true);
    const preview = await resolveFilePreview(file);
    setGalleryPreviewData(preview);
    setGalleryLoading(false);
  };

  useEffect(() => {
    if (!galleryDocument) return;
    loadGalleryPreview(galleryFiles[galleryIndex]);
  }, [galleryDocument, galleryFiles, galleryIndex]);

  useEffect(() => {
    let cancelled = false;
    const candidates = documents.flatMap((doc) => {
      if (!isDurchfuehrungMultiUploadDocument(doc)) return [];
      return [
        ...getDocumentFiles(phaseId, doc._id),
        ...getQueuedDocumentFiles(doc._id),
      ].slice(0, 4);
    });

    const uniqueCandidates = candidates.filter((file, index, allFiles) => {
      const key = getFilePreviewKey(file);
      return key && !file.previewData && !thumbnailCache[key] && allFiles.findIndex((entry) => getFilePreviewKey(entry) === key) === index;
    });

    if (uniqueCandidates.length === 0) return;

    uniqueCandidates.forEach((file) => {
      const key = getFilePreviewKey(file);
      void resolveFilePreview(file).then((preview) => {
        if (!preview || cancelled) return;
        setThumbnailCache((previous) => previous[key] ? previous : { ...previous, [key]: preview });
      });
    });

    return () => {
      cancelled = true;
    };
  }, [documents, phaseId, queueItems]);

  const handleDocumentSelect = async (document: DocumentType) => {
    if (document._id === 'seriennummer-ausseneinheit') {
      const serialNumber = getDocumentData(phaseId, 'seriennummer-ausseneinheit-text')
        || ((document as any).fileId as string | undefined);
      if (serialNumber) {
        setSerialPreviewTitle(document.name);
        setSerialPreviewText(serialNumber);
      } else if (onSelectDocument) {
        onSelectDocument(document);
      }
      return;
    }

    if (document._id === 'seriennummer-registrieren') {
      if (document.status === 'completed') {
        const raw = getDocumentData(phaseId, 'seriennummer-registrieren-text')
          || ((document as any).fileId as string | undefined);
        const display = raw ? formatConsentTimestamp(raw) : null;
        if (display) {
          setSerialPreviewTitle('Registrierung übermittelt');
          setSerialPreviewText(display);
        }
      } else if (onSelectDocument) {
        onSelectDocument(document);
      }
      return;
    }

    if (isDurchfuehrungMultiUploadDocument(document)) {
      const files = [
        ...getDocumentFiles(phaseId, document._id),
        ...getQueuedDocumentFiles(document._id),
      ];
      if (files.length > 0) {
        openDocumentGallery(document, files);
        return;
      }
      return;
    }

    // Wenn wir schon eine URL im Store haben (durch den Helper oben), nimm die!
    const localData = getDocumentData(phaseId, document._id);
    if (localData && localData.startsWith('https://drive.google.com/file/d/')) {
        await openDrivePreview(localData, document);
        return;
    }
    if (localData && localData.startsWith('http')) {
        setPreviewDocument(document);
        setPreviewData(localData);
        setPreviewModalOpen(true);
        return;
    }

    const hubspotDoc = document as any;
    if (hubspotDoc.fileId && hubspotDoc.status === "completed") {
      if (hubspotDoc.fileId.startsWith('https://drive.google.com/')) {
        await openDrivePreview(hubspotDoc.fileId, document);
      } else {
        handleOpenHubSpotDocument(hubspotDoc.fileId, document);
      }
      return;
    }
    if (document.status === "completed") {
      const storedFileId = getDocumentFileId(phaseId, document._id);
      if (storedFileId) {
        handleOpenHubSpotDocument(storedFileId, document);
      } else {
        handleOpenLocalDocumentPreview(document);
      }
      return;
    }
    if (hasFormularTemplate(document)) {
      handleOpenFormular(undefined, document);
    } else {
      if (onSelectDocument) onSelectDocument(document);
    }
  };

  const handleOpenLocalDocumentPreview = async (document: DocumentType) => {
    let data = getDocumentData(phaseId, document._id);
    if (!data) {
        const qItem = queueItems.find((i: any) => i.data?.documentId === document._id);
        if(qItem?.data?.imageData) data = qItem.data.imageData;
    }
    if(!data) {
      if (onSelectDocument) onSelectDocument(document);
      return;
    }
    setPreviewDocument(document);
    setPreviewData(data);
    setPreviewModalOpen(true);
  };

  const handleOpenHubSpotDocument = async (fileId: string, document: DocumentType) => {
    try {
      const url = await getSignedUrl(fileId);
      if(!url) throw new Error("No URL");
      setPreviewDocument(document);
      setPreviewData(url);
      setPreviewModalOpen(true);
    } catch(e) { console.error(e); alert("Fehler beim Laden."); }
  };

  const getDriveFileId = (docId: string): string | null => {
    const url = getDocumentData(phaseId, docId);
    if (!url?.startsWith('https://drive.google.com/file/d/')) return null;
    return url.split('/d/')[1]?.split('/')[0] ?? null;
  };

  const handleDeleteDocument = async (e: React.MouseEvent, docId: string) => {
    e.stopPropagation();
    if(window.confirm("Bist du sicher, dass du dieses Dokument löschen willst?")) {
      const driveFileId = getDriveFileId(docId);
      if (driveFileId) {
        try { await deleteDriveFile(driveFileId); } catch (err) { console.error('[Delete] Drive-Fehler:', err); }
      }
      await deleteDocument(phaseId, docId);
      setActionMenuOpen(null);
    }
  };

  const handleReplaceDocument = async (e: React.MouseEvent | undefined, document: DocumentType) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    setActionMenuOpen(null);
    if (isDurchfuehrungMultiUploadDocument(document)) {
      return;
    }
    // const driveFileId = getDriveFileId(document._id);
    // if (driveFileId) {
    //   try { await deleteDriveFile(driveFileId); } catch (err) { console.error('[Replace] Drive-Fehler:', err); }
    // }
    if (hasFormularTemplate(document)) {
      handleOpenFormular(undefined, document);
    } else {
      if (onSelectDocument) onSelectDocument(document);
    }
  };

  const toggleActionMenu = (e: React.MouseEvent, docId: string) => {
    e.stopPropagation();
    setActionMenuOpen(actionMenuOpen === docId ? null : docId);
  };

  const handleOpenFormular = (e: React.MouseEvent | undefined, document: DocumentType) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    setSelectedFormular(document);
    setFormularModalOpen(true);
  };

  // === Formular Logic ===
  const hasFormularTemplate = (doc: DocumentType) => {
    const ids = ["inbetriebnahmeprotokoll", "vdz-formular", "hydraulischer-abgleich"];
    return ids.includes(doc._id) || doc.name.toLowerCase().includes("inbetriebnahm") || doc.name.toLowerCase().includes("vdz");
  };

  const getTemplatePath = (docId: string) => {
    if(docId.includes("inbetriebnahm")) return "/Templates/Inbetriebnahmeprotokoll.html";
    return "/Templates/VdZ_Formular.html"; 
  };

  // UPDATE: Webhook Success Handler
  const handleFormularUploadSuccess = async (fileId?: string) => {
    if (!selectedFormular) return;
    
    console.log(`[CHECKLIST] Upload Success. ID: ${fileId}`);
    
    if (fileId) {
        // 1. ID speichern (setzt auch status auf "completed" im Store)
        saveDocumentFileId(phaseId, selectedFormular._id, fileId);
        
        // 2. AWAIT: Helper ruft URL ab und speichert sie
        // Damit wird das "alte" Bild sofort durch das PDF ersetzt
        await fetchAndCacheHubSpotUrl(fileId, selectedFormular._id);
    } else {
        // Fallback: Nur Status setzen wenn keine fileId vorhanden
        updateDocumentStatus(phaseId, selectedFormular._id, "completed");
    }
	  };

  const getUploadedFileKey = (file: UploadedDocumentFile) => file.fileId || file.url || file.id;

  const deleteDriveFileForReference = async (file: UploadedDocumentFile) => {
    const driveFileId = file.url
      ? getDriveFileIdFromUrl(file.url)
      : file.source === 'drive'
        ? file.fileId
        : null;

    if (!driveFileId) return;

    try {
      await deleteDriveFile(driveFileId);
    } catch (err) {
      console.error('[Delete] Drive-Fehler:', err);
    }
  };

  const syncMultiUploadProperty = async (document: DocumentType, files: UploadedDocumentFile[]) => {
    const dealId = phaseId.split('-')[0];
    const propertyName = DOCUMENT_TO_PROPERTY_MAP[document._id];
    if (!dealId || !propertyName) return;

    await updateDealTextProperty(
      dealId,
      propertyName,
      files.length > 0 ? uploadedDocumentFilesToPropertyValue(files) : ''
    );
  };

  const saveMultiUploadFiles = async (document: DocumentType, files: UploadedDocumentFile[]) => {
    saveDocumentFiles(phaseId, document._id, files);
    saveDocumentData(phaseId, document._id, files.find((file) => file.url)?.url || '');
    await syncMultiUploadProperty(document, files);
  };

  const handleDeleteAllImages = async (e: React.MouseEvent, document: DocumentType) => {
    e.stopPropagation();
    setActionMenuOpen(null);
    if (!window.confirm('Bist du sicher, dass du alle Bilder löschen willst?')) {
      return;
    }

    const files = getDocumentFiles(phaseId, document._id);
    queueItems
      .filter((item: any) => item.type === 'document_upload' && item.data?.documentId === document._id && item.data?.phaseId === phaseId)
      .forEach((item: any) => removeFromQueue(item.id));
    await Promise.all(files.map(deleteDriveFileForReference));
    await deleteDocument(phaseId, document._id);
    if (galleryDocument?._id === document._id) {
      closeDocumentGallery();
    }
  };

  const handleDeleteCurrentGalleryImage = async () => {
    if (!galleryDocument) return;
    const file = galleryFiles[galleryIndex];
    if (!file) return;

    if (!window.confirm('Bist du sicher, dass du dieses Bild löschen willst?')) {
      return;
    }

    const fileKey = getUploadedFileKey(file);
    const nextGalleryFiles = galleryFiles.filter((entry) => getUploadedFileKey(entry) !== fileKey);
    const nextStoredFiles = getDocumentFiles(phaseId, galleryDocument._id).filter(
      (entry) => getUploadedFileKey(entry) !== fileKey
    );

    await deleteDriveFileForReference(file);
    await saveMultiUploadFiles(galleryDocument, nextStoredFiles);

    if (nextGalleryFiles.length === 0) {
      closeDocumentGallery();
      return;
    }

    setGalleryFiles(nextGalleryFiles);
    setGalleryIndex((current) => Math.min(current, nextGalleryFiles.length - 1));
  };

  // === RENDER ===
  const getStatusBadge = (req: boolean) => req ? 
    <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Pflicht</span> : 
    <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Optional</span>;

  return (
    <div className="pb-24" data-testid="document-checklist">
      {loading && <div className="p-4 text-center">Lade...</div>}
      {error && <div className="p-4 text-red-500">{error}</div>}

      {!loading && !error && documents.map((doc) => {
        const canOpenExistingDocument = doc.status === 'completed';
        const isMultiUploadDocument = isDurchfuehrungMultiUploadDocument(doc);
        const uploadedFiles = isMultiUploadDocument ? getDocumentFiles(phaseId, doc._id) : [];
        const queuedFiles = isMultiUploadDocument ? getQueuedDocumentFiles(doc._id) : [];
        const displayFiles = [...uploadedFiles, ...queuedFiles];
	        const uploadedCount = displayFiles.length;
	        const uploadLabel = uploadedCount === 1 ? '1 Bild hochgeladen' : `${uploadedCount} Bilder hochgeladen`;
	        const visiblePreviewFiles = displayFiles.slice(0, 4);
	        const canPreviewDocument = isMultiUploadDocument
	          ? uploadedCount > 0
	          : Boolean((doc as any).fileId || getDocumentFileId(phaseId, doc._id) || getDocumentData(phaseId, doc._id));

	        return (
	        <div key={doc._id}
             data-testid="document-item"
             data-testid-card="document-card"
	             onClick={() => (!disabled || canOpenExistingDocument) && handleDocumentSelect(doc)}
	             className={`w-full bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4 transition-all ${doc.status === 'completed' ? 'border-l-4 border-green-500' : ''}`}>
	          {isMultiUploadDocument && (
            <input
              id={getDirectPhotoInputId(doc)}
              data-testid="direct-photo-input"
              data-document-id={doc._id}
              type="file"
              accept="image/*"
              multiple
              disabled={disabled}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => handleDirectPhotoInputChange(e, doc)}
              className="sr-only"
            />
	          )}

          <div className="flex justify-between mb-2">
            <div>
                <h3 className="font-bold text-gray-900 dark:text-white">{doc.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{doc.description}</p>
            </div>
            <div className="flex flex-col items-end gap-1">
                {getStatusBadge(doc.required)}
            </div>
          </div>

          {isMultiUploadDocument && uploadedCount > 0 && (
            <div className="mt-3">
              <div className="mb-2 text-sm font-medium text-green-700 dark:text-green-400">
                {uploadLabel}
              </div>
              <div className="flex gap-2 overflow-hidden" aria-label={`${doc.name} Upload-Vorschau`}>
	                {visiblePreviewFiles.map((file, index) => (
	                  (() => {
	                    const previewSrc = file.previewData || thumbnailCache[getFilePreviewKey(file)] || file.thumbnailUrl;
	                    return (
	                  <button
	                    key={file.id}
	                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDocumentGallery(doc, displayFiles);
                      setGalleryIndex(index);
                    }}
		                    className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-100 text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-700 dark:text-gray-300"
	                    aria-label={`${doc.name} Bild ${index + 1} anzeigen`}
	                  >
	                    {previewSrc ? (
	                      <img src={previewSrc} alt="" className="h-full w-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
	                    ) : (
	                      <span className="flex h-full w-full items-center justify-center">Lade...</span>
	                    )}
	                  </button>
	                    );
	                  })()
	                ))}
                {displayFiles.length > visiblePreviewFiles.length && (
	                  <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-50 text-xs font-medium text-gray-600 dark:border-gray-700 dark:bg-gray-700 dark:text-gray-200">
                    +{displayFiles.length - visiblePreviewFiles.length}
                  </div>
                )}
              </div>
            </div>
          )}

	          <div className="flex justify-between items-center mt-3">
		            {doc.status === 'completed' ? (
		                <button
		                  type="button"
		                  onClick={(e) => {
		                    e.stopPropagation();
		                    if (canPreviewDocument) {
		                      handleDocumentSelect(doc);
		                    }
		                  }}
		                  disabled={!canPreviewDocument}
		                  className="inline-flex items-center rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-700 transition-colors hover:border-green-300 hover:bg-green-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-500 dark:border-green-900/60 dark:bg-green-900/20 dark:text-green-300 dark:hover:bg-green-900/30 dark:disabled:border-gray-700 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
		                >
		                  <span className="mr-1">✓</span>
		                  {canPreviewDocument ? "Anzeigen" : "Wird verarbeitet..."}
		                </button>
		            ) : (
                isMultiUploadDocument ? (
                  <label
                    htmlFor={disabled ? undefined : getDirectPhotoInputId(doc)}
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    aria-disabled={disabled}
                    onClick={(e) => e.stopPropagation()}
                    className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      disabled
                        ? 'cursor-not-allowed bg-gray-300 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                        : 'cursor-pointer bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    Bilder hinzufügen
                  </label>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!disabled) {
                        handleDocumentSelect(doc);
                      }
                    }}
                    disabled={disabled}
                    className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600 dark:disabled:bg-gray-700 dark:disabled:text-gray-400"
                  >
                    Dokument hinzufügen
                  </button>
                )
	            )}

            {doc.status === 'completed' && (
                <div className="relative">
                    <button
                      type="button"
                      onClick={(e) => toggleActionMenu(e, doc._id)}
                      className="p-2 text-gray-500 dark:text-gray-400"
                      aria-label={`${doc.name} Aktionen`}
                      aria-expanded={actionMenuOpen === doc._id}
                    >
                      •••
                    </button>
                    {actionMenuOpen === doc._id && (
                        <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-slate-700 shadow-xl border border-gray-200 dark:border-slate-600 rounded z-50">
                            <button onClick={(e) => { e.stopPropagation(); handleDocumentSelect(doc); }} className="block w-full text-left px-4 py-2 text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-slate-600">Anzeigen</button>
                            {isMultiUploadDocument ? (
                              <>
                                <label
                                  htmlFor={getDirectPhotoInputId(doc)}
                                  role="button"
                                  tabIndex={0}
                                  onClick={(e) => e.stopPropagation()}
                                  className="block w-full cursor-pointer px-4 py-2 text-left text-gray-900 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-slate-600"
                                >
                                  Bilder hinzufügen
                                </label>
                                <button onClick={(e) => handleDeleteAllImages(e, doc)} className="block w-full text-left px-4 py-2 text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-slate-600">Alle Bilder löschen</button>
                              </>
                            ) : (
                              <>
                                <button onClick={(e) => handleReplaceDocument(e, doc)} className="block w-full text-left px-4 py-2 text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-slate-600">Ersetzen</button>
                                <button onClick={(e) => handleDeleteDocument(e, doc._id)} className="block w-full text-left px-4 py-2 text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-slate-600">Löschen</button>
                              </>
                            )}
                        </div>
                    )}
                </div>
            )}
          </div>
        </div>
        );
      })}

      {formularModalOpen && selectedFormular && (
        <FormularModal
          isOpen={formularModalOpen}
          onClose={() => { setFormularModalOpen(false); setSelectedFormular(null); }}
          templatePath={getTemplatePath(selectedFormular._id)}
          documentTitle={selectedFormular.name}
          dealId={phaseId.split('-')[0]}
          phaseId={phaseId}
          documentId={selectedFormular._id}
          onUploadComplete={handleFormularUploadSuccess}
          dealContact={dealContact}
        />
      )}

      {serialPreviewText !== null && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 px-0 sm:px-4" onClick={() => setSerialPreviewText(null)}>
          <div className="w-full sm:max-w-sm bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl px-6 py-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{serialPreviewTitle}</h2>
              <button onClick={() => setSerialPreviewText(null)} className="text-gray-500 hover:text-gray-800 dark:hover:text-white min-w-[44px] min-h-[44px] flex items-center justify-center">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p
              className="font-mono font-bold text-gray-900 dark:text-white text-center py-4 bg-gray-50 dark:bg-gray-700 rounded-xl whitespace-nowrap overflow-hidden"
              style={{ fontSize: `clamp(0.875rem, ${Math.min(2, 35 / Math.max(serialPreviewText.length, 1))}rem, 2rem)` }}
            >
              {serialPreviewText}
            </p>
          </div>
        </div>
      )}

      {previewModalOpen && previewDocument && (
        <DocumentPreviewModal
          isOpen={previewModalOpen}
          onClose={() => { setPreviewModalOpen(false); setPreviewDocument(null); }}
          documentName={previewDocument.name}
          documentType={previewData.startsWith("data:") ? "image" : "pdf"}
          previewData={previewData}
          onReplace={() => handleReplaceDocument(undefined, previewDocument)}
        />
      )}

      {galleryDocument && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4 pb-20" onClick={closeDocumentGallery}>
          <div className="relative flex h-full max-h-[calc(90vh-60px)] w-full max-w-7xl flex-col rounded-lg bg-white shadow-2xl dark:bg-gray-800" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
              <div className="min-w-0">
                <h2 className="truncate text-xl font-semibold text-gray-900 dark:text-white">{galleryDocument.name}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Bild {galleryIndex + 1} von {galleryFiles.length}
                </p>
              </div>
              <button
                onClick={closeDocumentGallery}
                className="flex min-h-[44px] min-w-[44px] items-center justify-center text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100"
                aria-label="Schließen"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex min-h-0 flex-1 flex-col bg-gray-50 p-3 dark:bg-gray-900">
              <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
	                {galleryFiles.map((file, index) => (
	                  (() => {
	                    const previewSrc = file.previewData || thumbnailCache[getFilePreviewKey(file)] || file.thumbnailUrl;
	                    return (
	                  <button
	                    key={file.id}
	                    type="button"
                    onClick={() => setGalleryIndex(index)}
	                    className={`h-20 w-20 flex-shrink-0 overflow-hidden rounded-md border text-xs ${
                      index === galleryIndex
                        ? 'border-primary-600 ring-2 ring-primary-200'
                        : 'border-gray-200 dark:border-gray-700'
                    } bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300`}
	                    aria-label={`Bild ${index + 1} anzeigen`}
	                  >
	                    {previewSrc ? (
	                      <img src={previewSrc} alt="" className="h-full w-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
	                    ) : (
	                      <span className="flex h-full w-full items-center justify-center">Lade...</span>
	                    )}
	                  </button>
	                    );
	                  })()
	                ))}
              </div>

              <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-auto rounded-lg bg-black">
                {galleryLoading ? (
                  <div className="text-sm text-white">Lade Bild...</div>
                ) : galleryPreviewData ? (
                  <img
                    src={galleryPreviewData}
                    alt={`${galleryDocument.name} ${galleryIndex + 1}`}
                    className="max-h-full max-w-full object-contain"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="text-sm text-white">Vorschau nicht verfügbar</div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 p-4 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setGalleryIndex((current) => Math.max(current - 1, 0))}
                disabled={galleryIndex === 0}
                className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 transition-colors disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-700 dark:text-gray-300"
              >
                Zurück
              </button>
              <div className="flex flex-1 justify-center gap-2">
                <label
                  htmlFor={disabled ? undefined : getDirectPhotoInputId(galleryDocument)}
                  role="button"
                  tabIndex={disabled ? -1 : 0}
                  aria-disabled={disabled}
                  onClick={(e) => e.stopPropagation()}
                  className={`rounded-lg px-4 py-2 transition-colors ${
                    disabled
                      ? 'cursor-not-allowed bg-gray-300 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                      : 'cursor-pointer bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  Bilder hinzufügen
                </label>
                <button
                  type="button"
                  onClick={handleDeleteCurrentGalleryImage}
                  className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-red-700 transition-colors hover:bg-red-100 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-300 dark:hover:bg-red-900/30"
                >
                  Bild löschen
                </button>
              </div>
              <button
                type="button"
                onClick={() => setGalleryIndex((current) => Math.min(current + 1, galleryFiles.length - 1))}
                disabled={galleryIndex >= galleryFiles.length - 1}
                className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 transition-colors disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-700 dark:text-gray-300"
              >
                Weiter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentChecklist;
